import React, { useState, useRef, useContext } from 'react';

import { useIntl, FormattedMessage } from 'react-intl';

import OmView from '@xingternal/xing-view/OmView'; // eslint-disable-line
import { BodyCopy } from '@xingternal/typography';
import { usePopOver } from '@xingternal/pop-over';
import { Menu } from '@xingternal/menu';
import SelectList, { SelectListOption } from '@xingternal/malt-select-list';
import { IconImage, IconUpload } from '@xingternal/icons';
import { trackEditOpening, trackEditSaving } from '@xingternal/pages-tracking';

import {
  ENTITY_PAGE_QUERY,
  getEntityPageQueryVariables,
  ExternalEditInfoBanner,
  ENTITY_SUBPAGE_QUERY,
  getEntitySubpageQueryVariables,
  EditButton,
  ImageUpload,
  DialogContextProvider,
  DialogContext,
  PageContext,
  NotSavedDialog,
  SmallGrid,
} from '@xingternal/pages-common';

import { UpdateCrop } from '../Common';

import { ErrorContext } from '@xingternal/pages-page-error';

import styles from '../edit.css';

import { useEntityPageUpdateLogoMutation } from './UpdateLogo.gql-types';
import { useRouteMatch } from 'react-router-dom';

const EditLogoMenu = (props: any) => (
  <DialogContextProvider>
    <RenderEditLogoMenu {...props} />
  </DialogContextProvider>
);

type RenderEditLogoMenuProps = {
  setNewLogo: (logo: any) => void;
  setNewOriginalLogo: (originalLogo: any) => void;
  newOriginalLogo?: string;
  pageSlug: string;
  hasLogo: boolean;
  match?: {
    params?: {
      type?: string;
    };
  };
};

type MatchProps = {
  match: {
    params?: {
      type?: string;
    };
  };
};

const RenderEditLogoMenu = ({
  pageSlug,
  setNewLogo,
  setNewOriginalLogo,
  newOriginalLogo,
  hasLogo,
}: RenderEditLogoMenuProps) => {
  const popOver = usePopOver();
  const [isEditCrop, setIsEditCrop] = useState(null);
  const match = useRouteMatch<any | MatchProps>();
  const { showError } = useContext(ErrorContext);
  const { pageContext } = useContext(PageContext);
  const intl = useIntl();
  let locale = intl.locale;

  const closeOmView = useRef<any | undefined>(null);

  const logoForCache = useRef(null);

  const displayError = (error: any) =>
    showError({
      message: 'EP_GENERAL_FEEDBACK_ERROR',
      error,
    });

  const [updateLogo] = useEntityPageUpdateLogoMutation({
    onCompleted: (data) =>
      data?.entityPageUpdateLogo?.error &&
      displayError(data.entityPageUpdateLogo.error),
    onError: (error) => displayError(error),
    update: (cache) => {
      const entityPageData: any = cache.readQuery({
        query: ENTITY_PAGE_QUERY,
        variables: getEntityPageQueryVariables(
          pageContext?.pageSlug,
        ),
      });

      cache.writeQuery({
        query: ENTITY_PAGE_QUERY,
        variables: getEntityPageQueryVariables(
          pageContext?.pageSlug,
        ),
        data: {
          entityPageEX: {
            ...entityPageData.entityPageEX,
            logoImage: [
              {
                ...entityPageData.entityPageEX.logoImage[0],
                url: logoForCache.current,
              },
            ],
          },
        },
      });

      // trys to get subpage data on the cache. (might still not exist)
      try {
        const entitySubpageData: any = cache.readQuery({
          query: ENTITY_SUBPAGE_QUERY,
          variables: getEntitySubpageQueryVariables(
            pageContext?.pageSlug,
            match.params?.type
          ),
        });

        cache.writeQuery({
          query: ENTITY_SUBPAGE_QUERY,
          variables: getEntitySubpageQueryVariables(
            pageContext?.pageSlug,
            match.params?.type
          ),
          data: {
            entityPageEX: {
              ...entitySubpageData.entityPageEX,
              logoImage: [
                {
                  ...entitySubpageData.entityPageEX.logoImage[0],
                  url: logoForCache.current,
                },
              ],
            },
          },
        });
      } catch {
        return;
      }
    },
  });

  const {
    setDataChanged,
    dataChanged,
    dialogConfirmation,
    setDialogConfirmation,
  }: any = useContext(DialogContext);

  const handleClick = (activateOmView?: any, isEditCrop?: any) => {
    setIsEditCrop(isEditCrop);
    popOver.handleHide();
    activateOmView();
  };

  const onUpload = async (
    pageSlug: string,
    logoUploadId: string,
    originalLogoUploadId: string,
    handleClose: () => void,
    logo: any,
    originalLogo: any
  ) => {
    logoForCache.current = logo;

    const { data } = await updateLogo({
      variables: {
        id: pageSlug,
        logoUploadId,
        originalLogoUploadId,
      },
    });

    if (!data?.entityPageUpdateLogo?.error) {
      setNewLogo(logo);
      setNewOriginalLogo(originalLogo);
      setDataChanged(false);
      handleClose();
    }
  };

  const discard = () => {
    if (dataChanged) {
      setDialogConfirmation({
        dialogAction: () => {
          setDialogConfirmation(null);
          setDataChanged(false);
          closeOmView.current();
        },
      });
    } else {
      closeOmView.current();
    }
  };

  return (
    <>
      <EditButton
        size={'small'}
        showIcon
        className={styles.logoEditButton}
        data-cy="EDIT_BUTTON_ON_LOGO"
        data-testid={'EDIT_BUTTON_ON_LOGO'}
        onClick={popOver.handleShow}
        innerRef={popOver.triggerRef}
      />
      <OmView
        interceptClose={() => discard()}
        trigger={(activateOmView: any) => (
          <Menu
            dimmerTitle="close menu"
            onOutsideClick={popOver.handleHide}
            triggerRef={popOver.triggerRef}
            show={popOver.show}
          >
            <span>
              <SelectList className={styles.listWrapper}>
                <SelectListOption
                  className={styles.listItem}
                  data-cy="LOGO_UPLOAD"
                  data-testid={'LOGO_UPLOAD'}
                  onClick={() => {
                    trackEditOpening({
                      focusType: pageContext?.focusType,
                      itemId: pageContext?.pageId,
                      module: 'header',
                      part: 'logo',
                    });
                    handleClick(activateOmView);
                  }}
                >
                  <IconUpload width={24} height={24} />
                  <BodyCopy noMargin size={'small'} className={styles.link}>
                    <FormattedMessage
                      id="EP_UPLOAD_LOGO"
                      defaultMessage="EP_UPLOAD_LOGO"
                    />
                  </BodyCopy>
                </SelectListOption>
                {hasLogo && (
                  <SelectListOption
                    className={styles.listItem}
                    data-cy="LOGO_EDIT"
                    onClick={() => {
                      trackEditOpening({
                        focusType: pageContext?.focusType,
                        itemId: pageContext?.pageId,
                        module: 'header',
                        part: 'logo',
                      });
                      handleClick(activateOmView, true);
                    }}
                  >
                    <IconImage width={24} height={24} />
                    <BodyCopy noMargin size={'small'} className={styles.link}>
                      <FormattedMessage
                        id="EP_EDIT_SECTION"
                        defaultMessage="EP_EDIT_SECTION"
                      />
                    </BodyCopy>
                  </SelectListOption>
                )}
              </SelectList>
            </span>
          </Menu>
        )}
      >
        {({ handleClose }: any) => {
          closeOmView.current = handleClose;
          return (
            <SmallGrid>
              {isEditCrop ? (
                <UpdateCrop
                  setDataChanged={setDataChanged}
                  setNewLogo={setNewLogo}
                  onDragCrop={() => setDataChanged(true)}
                  onDiscard={() => discard()}
                  newOriginal={newOriginalLogo}
                  pageSlug={pageSlug}
                  closeOmview={handleClose}
                  imageType={'logo'}
                  showError={showError}
                  onPublish={() => {
                    trackEditSaving({
                      focusType: pageContext?.focusType,
                      itemId: pageContext?.pageId,
                      module: 'header',
                      part: 'logo',
                    });
                  }}
                />
              ) : (
                <ImageUpload
                  onDiscard={() => discard()}
                  onChange={() => setDataChanged(true)}
                  showError={showError}
                  shouldUploadOriginal={true}
                  onPublish={(
                    logoUploadId,
                    logo,
                    originalLogoUploadId,
                    originalLogo
                  ) => {
                    trackEditSaving({
                      focusType: pageContext?.focusType,
                      itemId: pageContext?.pageId,
                      module: 'header',
                      part: 'logo',
                    });
                    onUpload(
                      pageSlug,
                      logoUploadId,
                      originalLogoUploadId,
                      handleClose,
                      logo,
                      originalLogo
                    );
                  }}
                  onCancel={handleClose}
                  imageUploadCopy={{
                    headline: 'EP_CHOOSE_LOGO',
                    hint: 'EP_UPLOAD_LOGO_HINT',
                    label: 'EP_UPLOAD_IMAGE_LABEL',
                    onDragLabel: 'EP_UPLOAD_IMAGE_DRAG_LABEL',
                  }}
                />
              )}
              <ExternalEditInfoBanner
                copyKey="EP_UPLOAD_LOGO_EDIT_INFO"
                ctaUrl={`https://faq.xing.com/${locale}/node/68249`}
                ctaCopyKey="EP_UPLOAD_LOGO_EDIT_INFO_CTA"
              />
            </SmallGrid>
          );
        }}
      </OmView>
      {dataChanged && dialogConfirmation?.dialogAction && <NotSavedDialog />}
    </>
  );
};

export default EditLogoMenu;
